'use client';

import { useState } from 'react';

import { CircularProgressIcon } from '@wwe-portal/ui/components/icons/circular-progress-icon';
import {
  Dialog,
  DialogContent,
  CommonDialogContentLayout,
  ProgressingDialogHead,
} from '@wwe-portal/ui/components/dialog';
import successLogo from '@wwe-portal/ui/assets/images/common/payment-success.webp';

interface BaseDialogState {
  title: string;
  description: string;
}

interface PaymentProgressingDialogState extends BaseDialogState {
  duration: number;
}

export interface UsePaymentDialogProps {
  onClose?: () => void;
}

export const usePaymentDialog = (props?: UsePaymentDialogProps) => {
  const [status, setStatus] = useState<'closed' | 'progress' | 'success'>(
    'closed'
  );
  const [paymentProgress, setPaymentProgress] = useState<number>();
  const [paymentProgressState, setPaymentProgressState] =
    useState<PaymentProgressingDialogState>();
  const [successState, setSuccessState] = useState<BaseDialogState>();

  const closeAll = () => {
    setSuccessState(undefined);
    setPaymentProgress(undefined);
    setPaymentProgressState(undefined);
    setStatus('closed');
  };

  const handleClose = () => {
    closeAll();
    props?.onClose?.();
  };

  const dialog = (
    <Dialog
      onOpenChange={(value) => {
        if (!value && status === 'success') {
          handleClose();
        }
      }}
      open={status !== 'closed'}
    >
      <DialogContent
        data-id={status === 'progress' ? 'progress-dialog' : 'success-dialog'}
        variant="message"
      >
        {status === 'progress' &&
          paymentProgressState != null &&
          paymentProgress != null && (
            <CommonDialogContentLayout
              actionElement={undefined}
              closable={false}
              descriptionElement={paymentProgressState.description}
              imageElement={
                <ProgressingDialogHead
                  duration={paymentProgressState.duration}
                  progressPercent={paymentProgress}
                />
              }
              titleElement={paymentProgressState.title}
            />
          )}
        {status === 'success' && successState != null && (
          <CommonDialogContentLayout
            actionElement={undefined}
            closable
            descriptionElement={successState.description}
            imageElement={
              <div className="relative flex h-full w-full items-center justify-center">
                <img alt="success" src={successLogo} />
                <div className="absolute inset-0">
                  <CircularProgressIcon
                    duration={0}
                    height="100%"
                    progressPercent={100}
                    width="100%"
                  />
                </div>
              </div>
            }
            onClose={handleClose}
            titleElement={successState.title}
          />
        )}
      </DialogContent>
    </Dialog>
  );

  const showSuccess = (nextSuccessState: {
    title: string;
    description: string;
  }) => {
    setPaymentProgressState(undefined);
    setStatus('success');
    setSuccessState(nextSuccessState);
  };

  const startProgress = (
    nextProgressState: {
      title: string;
      description: string;
      duration: number;
    },
    to: number
  ) => {
    setStatus('progress');
    setPaymentProgressState(nextProgressState);
    setPaymentProgress(0);
    setTimeout(() => {
      setPaymentProgress(to);
    });
  };

  return {
    status,
    dialog,
    showSuccess,
    startProgress,
    closeAll,
  };
};
